import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Carte, Erreur, Vide } from '../../composants/communs.tsx';
import { ImageProtegee } from '../../composants/ImageProtegee.tsx';
import type { PhotoProduit, Produit } from './types.ts';
import { COTE_MAX, envoyerPhoto } from './reduire-image.ts';

/**
 * Photos d'un produit — RG-019a.
 *
 * Zéro, une, ou plusieurs. Ce sont des ILLUSTRATIONS de catalogue : elles se
 * suppriment, contrairement aux photos de panier qui sont des pièces de
 * preuve.
 */

export function Photos({
  produit,
  modifiable,
  onFerme,
}: {
  produit: Produit;
  modifiable: boolean;
  onFerme: () => void;
}): ReactNode {
  const client = useQueryClient();
  const champ = useRef<HTMLInputElement>(null);
  const [erreurEnvoi, setErreurEnvoi] = useState<unknown>(null);

  const { data, isPending } = useQuery({
    queryKey: ['photos-produit', produit.id],
    queryFn: () =>
      appeler<{ donnees: PhotoProduit[] }>(`/produits/${produit.id}/photos`),
    select: (reponse) => reponse.donnees,
  });

  function rafraichir(): void {
    void client.invalidateQueries({ queryKey: ['photos-produit', produit.id] });
  }

  /**
   * L'envoi passe par `fetch` directement, pas par le client d'API.
   *
   * Un corps `multipart` ne peut pas porter d'en-tête `content-type` fixé à
   * la main : le navigateur doit y écrire lui-même la frontière du corps.
   */
  const envoi = useMutation({
    mutationFn: async (fichier: File) => {
      return envoyerPhoto<PhotoProduit>(produit.id, fichier);
    },
    onSuccess: () => {
      setErreurEnvoi(null);
      rafraichir();
    },
    onError: setErreurEnvoi,
  });

  const suppression = useMutation({
    mutationFn: (photoId: string) =>
      appeler(`/photos-produit/${photoId}`, { methode: 'DELETE' }),
    onSuccess: rafraichir,
  });

  return (
    <Carte
      titre={`Photos — ${produit.designation}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      {modifiable && (
        <div className="ligne" style={{ marginBottom: 14 }}>
          <input
            ref={champ}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label="Ajouter une photo"
            style={{ maxWidth: 320 }}
            onChange={(evenement) => {
              const fichier = evenement.target.files?.[0];
              if (fichier !== undefined) {
                envoi.mutate(fichier);
              }
              evenement.target.value = '';
            }}
          />
          <span className="entete__sous">
            Réduite à {COTE_MAX} px avant l’envoi.
          </span>
        </div>
      )}

      <Erreur erreur={erreurEnvoi} />
      <Erreur erreur={suppression.error} />

      {isPending ? (
        <Vide texte="Chargement…" />
      ) : data === undefined || data.length === 0 ? (
        <Vide texte="Aucune photo. Ce produit s’affichera sans illustration." />
      ) : (
        <div className="grille">
          {data.map((photo) => (
            <figure
              key={photo.id}
              className="carte"
              style={{ margin: 0, padding: 10 }}
            >
              <ImageProtegee
                chemin={`/photos-produit/${photo.id}`}
                alt={`${produit.designation} — vue ${String(photo.position + 1)}`}
              />
              <figcaption
                className="ligne"
                style={{ justifyContent: 'space-between', marginTop: 8 }}
              >
                <span className="entete__sous">
                  {photo.largeur === null
                    ? '—'
                    : `${String(photo.largeur)} × ${String(photo.hauteur ?? 0)}`}
                  {' · '}
                  {Math.round(photo.taille_octets / 1024)} Ko
                </span>
                {modifiable && (
                  <button
                    type="button"
                    className="bouton--discret"
                    disabled={suppression.isPending}
                    onClick={() => {
                      suppression.mutate(photo.id);
                    }}
                  >
                    Retirer
                  </button>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </Carte>
  );
}
